import type { SupabaseClient } from '@supabase/supabase-js'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import {
  type AlternativeUpdate,
  type QuoteLineForAlternatives,
  isBeforeCustomerApproval,
} from 'src/shared/utils/quoteAlternatives'
import type { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'

/**
 * Why a save of alternatives did not fully land. `partial` means some rows
 * were written before a failure; `restored` says whether those were put back.
 */
export class AlternativesSaveError extends Error {
  constructor(
    readonly kind: 'failed' | 'partial',
    readonly restored: boolean,
    readonly cause?: unknown
  ) {
    super(`alternatives save ${kind}${restored ? '' : ', not restored'}`)
    this.name = 'AlternativesSaveError'
  }
}

/** One row; throws unless exactly that row was updated (RLS can match none). */
const writeRow = async (
  supabase: SupabaseClient<Database>,
  { id, slot, is_recommended }: AlternativeUpdate
) => {
  const { data, error } = await supabase
    .from('quote_item')
    .update({ slot, is_recommended })
    .eq('id', id)
    .select('id, slot, is_recommended')
  if (error) throw error
  const row = data?.[0]
  if (
    data?.length !== 1 ||
    (row.slot ?? null) !== slot ||
    !!row.is_recommended !== is_recommended
  ) {
    throw new Error(`quote_item ${id} was not updated`)
  }
}

/**
 * Refuses when any of these rows' quote is past customer approval or already
 * has a recorded choice (e.g. a dialog opened before the customer approved).
 * Checked right before writing; the database itself has no such lock yet.
 */
const assertStillEditable = async (
  supabase: SupabaseClient<Database>,
  ids: number[]
) => {
  const { data: rows, error } = await supabase
    .from('quote_item')
    .select('quote_id, quote!inner(status)')
    .in('id', ids)
  if (error) throw error
  const quoteIds = [...new Set((rows ?? []).map((row) => row.quote_id))]
  if (rows?.some((row) => !isBeforeCustomerApproval(row.quote?.status))) {
    throw new AlternativesSaveError('failed', true)
  }
  const { data: chosen, error: chosenError } = await supabase
    .from('quote_item')
    .select('id')
    .in('quote_id', quoteIds)
    .not('customer_selected_at', 'is', null)
    .limit(1)
  if (chosenError) throw chosenError
  if (chosen?.length) throw new AlternativesSaveError('failed', true)
}

/**
 * Writes an alternatives edit (see the `plan*` helpers in quoteAlternatives)
 * row by row, checking each row really changed. On a failure part-way, the
 * rows already written are put back to their values in `before`.
 *
 * Separate requests, not one transaction (no RPC yet), so a failure of the
 * restore itself is possible; the error then says so.
 */
export const writeAlternativeUpdates = async (
  supabase: SupabaseClient<Database>,
  updates: AlternativeUpdate[],
  before: readonly QuoteLineForAlternatives[]
) => {
  const written: AlternativeUpdate[] = []
  try {
    for (const update of updates) {
      await writeRow(supabase, update)
      written.push(update)
    }
  } catch (cause) {
    let restored = true
    for (const done of [...written].reverse()) {
      const was = before.find((line) => line.id === done.id)
      try {
        await writeRow(supabase, {
          id: done.id,
          slot: was?.slot?.trim() || null,
          is_recommended: !!was?.is_recommended,
        })
      } catch (restoreError) {
        restored = false
        console.error('[alternatives] restore failed', restoreError)
      }
    }
    throw new AlternativesSaveError(
      written.length ? 'partial' : 'failed',
      restored,
      cause
    )
  }
}

/** Toast text for a failed alternatives save. */
export const alternativesErrorText = (
  error: unknown,
  t: (en: string, sv?: string) => string
) =>
  error instanceof AlternativesSaveError &&
  error.kind === 'partial' &&
  !error.restored
    ? t(
        'Only part of the change was saved and could not be undone. Refresh and check the alternatives.',
        'Bara en del av ändringen sparades och kunde inte ångras. Ladda om och kontrollera alternativen.'
      )
    : t(
        'Nothing was changed. Refresh and try again.',
        'Inget ändrades. Ladda om och försök igen.'
      )

export const useQuoteAlternativesApi = () => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()
  const { toast } = useToast()
  const { t } = useLocalization()

  const saveAlternatives = useMutation({
    mutationFn: async ({
      updates,
      lines,
    }: {
      updates: AlternativeUpdate[]
      /** The rows as they were before this edit, for the restore. */
      lines: readonly QuoteLineForAlternatives[]
    }) => {
      if (!updates.length) return
      await assertStillEditable(
        supabase,
        updates.map((update) => update.id)
      )
      await writeAlternativeUpdates(supabase, updates, lines)
    },
    onSuccess: () => {
      toast({ title: t('Alternatives updated', 'Alternativ uppdaterade') })
    },
    onError: (error) => {
      console.error('[alternatives] update failed', error)
      toast({
        variant: 'destructive',
        title: t(
          'Could not update the alternatives',
          'Kunde inte uppdatera alternativen'
        ),
        description: alternativesErrorText(error, t),
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItems] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItemById] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quotes] })
    },
  })

  return { saveAlternatives }
}
